<template>
    <div class="tile tile-secondary">
        <div class="tile-title">
            <h2>KYC</h2>
            <add-document-modal class="no-flex" @submit="saveDocument"/>
        </div>
        <div class="tile-content">
            <ul v-if="hasDocuments" class="list list-group">
                <li v-for="document in documents" class="list-item">
                    <v-icon slot="left-icon" v-bind="getIconProps(document)" class="no-flex inline-s"/>
                    <review-document-modal
                        :document="document"
                        class="grid-item"
                        :key="document.id"
                        @accept="acceptDocument"
                        @reject="rejectDocument"/>
                    <r-badge :type="getBadgeType(document)" class="no-flex">{{document.status | unKebabCase}}</r-badge>
                </li>
            </ul>
            <span v-else class="text-muted">There is no documents associated with this customer</span>
        </div>
    </div>
</template>

<script>
    import vIcon from '@/components/form/v-icon';
    import KYCDocumentModel from '@/common/models/kyc-document-model';
    import LoadStateMixin from '@/mixins/load-state-mixin';
    import AddDocumentModal from './add-document-modal';
    import ReviewDocumentModal from './review-document-modal';

    export default {
        name: 'kyc-manager',
        mixins: [LoadStateMixin],
        components: {
            vIcon,
            AddDocumentModal,
            ReviewDocumentModal,
        },
        props: {
            customer: {
                type: Object,
            },
        },
        data() {
            return {
                documents: [],
                syncReviewHandle: null,
                syncReviewDelay: 5000,
            };
        },
        computed: {
            missingReviewDocuments() {
                return this.documents.filter(({hasReview}) => !hasReview);
            },
            hasDocuments() {
                return this.documents.length;
            },
        },
        methods: {
            getIconProps({hasReview}) {
                if (!hasReview) {
                    return {
                        class: 'is-spinning icon-light-gray',
                        icon: 'loading',
                    };
                }
                return {
                    icon: 'clip',
                };
            },
            getBadgeType({status}) {
                const statusMap = KYCDocumentModel.DocumentStatus;
                const badgeTypeStatusMap = {
                    [statusMap.accepted]: 'positive',
                    [statusMap.pending]: 'warning',
                    [statusMap.inProgress]: 'warning',
                    [statusMap.rejected]: 'negative',
                };
                return badgeTypeStatusMap[status];
            },
            acceptDocument({id}) {
                this.review('accept', {id});
            },
            rejectDocument({id}, {type, message}) {
                this.review('reject', {id, data: {type, message}});
            },
            checkSyncReviews() {
                if (this.missingReviewDocuments.length) {
                    this.startSyncReviews(this.missingReviewDocuments);
                }
            },
            startSyncReviews(missingReviewDocuments) {
                this.stopSyncReviews();
                const delayedAction = (
                    (context, documents) => async () => {
                        const checkAgain = await context.syncReviewDocuments(documents);
                        if (checkAgain) {
                            context.checkSyncReviews();
                        }
                    }
                )(this, missingReviewDocuments);
                this.syncReviewHandle = setTimeout(delayedAction, this.syncReviewDelay);
            },
            stopSyncReviews() {
                clearTimeout(this.syncReviewHandle);
                this.syncReviewHandle = null;
            },
            async syncReviewDocuments(missingReviewDocuments) {
                const initialHandler = this.syncReviewHandle;
                try {
                    const syncDocuments = await this.fetchDocuments(missingReviewDocuments);
                    // Check if handler change while network call
                    if (initialHandler !== this.syncReviewHandle) {
                        return false;
                    }
                    // Still same handler
                    const documentsToUpdate = syncDocuments.filter(({hasReview}) => hasReview);
                    documentsToUpdate.forEach(
                        (document) => {
                            const index = this.documents.findIndex(
                                currDoc => currDoc.id === document.id,
                            );
                            this.documents.splice(index, 1, document);
                        },
                    );
                    if (documentsToUpdate.length === missingReviewDocuments.length) {
                        return false;
                    }
                } catch (error) {
                    return false;
                }
                return true;
            },
            async saveDocument(blobDocumentImage) {
                const form = new FormData();
                form.append('file', blobDocumentImage);
                try {
                    const {fields: file} = await this.$api(
                        ({files}) => files.upload({fileObject: form}),
                    );
                    const paramsKYC = {
                        data: {
                            fileId: file.id,
                            customerId: this.customer.id,
                            documentType: 'identity-proof',
                        },
                    };
                    const {fields} = await this.$api(
                        ({kycDocuments}) => kycDocuments.create(paramsKYC),
                    );
                    this.documents.push(new KYCDocumentModel(fields));
                    this.$toast.positive('Document successfully saved');
                    this.$emit('updated');
                } catch (error) {
                    this.$toast.fromError(error, 'Document could not be saved');
                }
            },
            async review(action, params) {
                try {
                    const {fields} = await this.$api(
                        ({kycDocuments}) => kycDocuments[action](params),
                    );
                    this.documents = this.documents.map(
                        document => (document.id === fields.id ? new KYCDocumentModel(fields) : document),
                    );
                    this.$toast.positive(`Document successfully ${action}ed`);
                    this.$emit('updated');
                } catch (error) {
                    this.$toast.negative(`Document could not be ${action}ed`);
                }
            },
            async fetchDocuments(documents) {
                const params = {};
                if (documents) {
                    const ids = documents.map(({id}) => id).join(',');
                    params.filter = `id:${ids}`;
                } else {
                    params.filter = `customerId:${this.customer.id}`;
                }
                const {items} = await this.$api(({kycDocuments}) => kycDocuments.getAll(params));
                return items.map(({fields}) => new KYCDocumentModel(fields));
            },
            async fetchData() {
                this.documents = await this.fetchDocuments();
            },
        },
        watch: {
            documents() {
                this.checkSyncReviews();
            },
        },
        beforeDestroy() {
            this.stopSyncReviews();
        },
    };
</script>
